import { Option } from '@sapphire/result';

import { Nullable, spreadIfSome } from '@ps/utils';

import { MC_GROUPED_UPDATE_HOIST_EXCEPTIONS } from '../../constants/mc.constants';
import {
  McGroupedCreateChildPayloadDto,
  McGroupedCreatePayloadDto,
  McGroupedUpdatePayloadDto,
} from '../../dtos/mc-grouped-update.payload.dto';
import { StandardSurveyPayloadDto } from '../../dtos/standard-survey.payload.dto';
import { sumBy } from '../../helpers/quota-allocation/quota-allocation.helper';
import { hoistCommonFields } from '../hoist-common-fields.helper';
import {
  ProjectPayloadInput,
  ProjectPayloadMapper,
  ProjectUpdatePayloadInput,
} from '../project-payload-mapper/project.payload.mapper';
import { StandardSurveyPayloadInput } from '../standard-survey-payload-mapper/standard-survey.payload.mapper';

export class McGroupedUpdatePayloadMapper extends ProjectPayloadMapper {
  public toCreatePayload(
    input: ProjectPayloadInput,
  ): McGroupedCreatePayloadDto {
    const childPayloads = this.childPayloads(input);

    return {
      ...this.toProjectPayload(
        this.withoutLocalization(childPayloads[0]),
        childPayloads.map((payload, index) =>
          this.child(payload, index, input),
        ),
      ),
      survey_localizations: this._localizations(childPayloads),
    };
  }

  public toUpdatePayload({
    project,
    children,
  }: ProjectUpdatePayloadInput): McGroupedUpdatePayloadDto {
    const childPayloads = children.map(({ surveyId, ...child }) => {
      const payload = this.standardMapper.toPayload(child);
      return {
        ...payload,
        offer_price: numericOfferPrice(payload.offer_price),
        survey_id: surveyId,
      };
    });

    const { root, children: hoistedChildPayloads } = hoistCommonFields(
      this._updateRoot(project, childPayloads),
      childPayloads,
      MC_GROUPED_UPDATE_HOIST_EXCEPTIONS,
    );

    return {
      ...this.toProjectPayload(root, hoistedChildPayloads),
      survey_localizations: this._localizations(childPayloads),
    };
  }

  protected override child(
    payload: StandardSurveyPayloadDto,
    index: number,
    input: ProjectPayloadInput,
  ): McGroupedCreateChildPayloadDto {
    return {
      survey_localization: payload.survey_localization,
      ...super.child(payload, index, input),
    };
  }

  private _localizations(
    payloads: readonly StandardSurveyPayloadDto[],
  ): string[] {
    return payloads.map(({ survey_localization }) => survey_localization);
  }

  private _updateRoot(
    project: StandardSurveyPayloadInput,
    children: readonly { completes_required: number; offer_price: number }[],
  ): Omit<StandardSurveyPayloadDto, 'survey_localization'> {
    const { offer_price: _activeLocaleOfferPrice, ...root } =
      this.withoutLocalization(this.standardMapper.toPayload(project));

    return {
      ...root,
      ...spreadIfSome(sharedOfferPrice(children), (offer_price) => ({
        offer_price,
      })),
      completes_required: sumBy(
        children,
        ({ completes_required }) => completes_required,
      ),
      qualifications: [],
      quotas: [],
    };
  }
}

function numericOfferPrice(offerPrice: number | undefined): number {
  return Option.from(offerPrice).unwrapOr(0);
}

function sharedOfferPrice(
  children: readonly { offer_price: number }[],
): Nullable<number> {
  const prices = new Set(children.map(({ offer_price }) => offer_price));
  return prices.size === 1 ? [...prices][0] : null;
}
